import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faInstagram, faFacebook } from '@fortawesome/free-brands-svg-icons';
import LazyImage from './LazyImage';
import SkeletonLoader from './SkeletonLoader';
import ErrorMessage from './ErrorMessage';
import SEOHead from './SEOHead';
import './BenefitDetail.css';

const getInitials = (partner) => {
  if (!partner) return '?';
  const words = partner.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
};

const isValidUrl = (url) => {
  // Validate the URL to prevent XSS attacks.
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols.
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    // If the URL is relative, check it does not start with a dangerous protocol.
    const trimmedUrl = url.trim().toLowerCase();
    const dangerousProtocols = ['javascript', 'data', 'vbscript'];
    return !dangerousProtocols.some((protocol) => trimmedUrl.startsWith(protocol + ':'));
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  // Force UTC so a date-only string like "2024-12-31" doesn't shift a day
  // backward in timezones behind UTC.
  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatValidity = (startDate, endDate) => {
  const end = endDate ? formatDate(endDate) : null;
  if (!end) return null;
  const start = startDate ? formatDate(startDate) : null;
  return start ? `Válido del ${start} al ${end}` : `Válido hasta el ${end}`;
};

const formatDiscountHeadline = (benefit) => {
  if (benefit.discount_pct && benefit.discount_pct > 0) {
    return `${benefit.discount_pct}% OFF`;
  }
  if (benefit.discount_price && benefit.list_price) {
    return `Antes $${benefit.list_price}`;
  }
  return null;
};

const SocialIcons = ({ benefit }) => (
  <div className="benefit-detail__social-icons">
    {benefit.instagram && isValidUrl(benefit.instagram) && (
      <a href={benefit.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
        <FontAwesomeIcon icon={faInstagram} />
      </a>
    )}
    {benefit.whatsapp && (
      <a
        href={`https://wa.me/${benefit.whatsapp}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
      >
        <FontAwesomeIcon icon={faWhatsapp} />
      </a>
    )}
    {benefit.facebook && isValidUrl(benefit.facebook) && (
      <a href={benefit.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
        <FontAwesomeIcon icon={faFacebook} />
      </a>
    )}
  </div>
);

const BenefitDetail = ({ benefits, loading, retry }) => {
  const { slug } = useParams();
  const [copied, setCopied] = useState(false);

  const benefit = benefits.find((b) => b.slug === slug);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(benefit.promo_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable or denied — the code still shows as text.
    }
  };

  if (loading) {
    return (
      <div className="benefit-detail">
        <SkeletonLoader variant="detail" />
      </div>
    );
  }

  if (!benefit) {
    return (
      <div className="benefit-detail">
        <ErrorMessage
          message="No encontramos este beneficio. Es posible que ya no esté disponible."
          onRetry={retry}
          type="warning"
        />
      </div>
    );
  }

  const validity = formatValidity(benefit.start_date, benefit.end_date);
  const discountHeadline = formatDiscountHeadline(benefit);
  const infoLines = [
    benefit.web && { label: 'Web', value: benefit.web },
    benefit.address && { label: 'Dirección', value: benefit.address },
    benefit.phone && { label: 'Celular de atención', value: benefit.phone },
    benefit.email && { label: 'Correo', value: benefit.email },
  ].filter(Boolean);

  return (
    <div className="benefit-detail">
      <SEOHead
        title={`${benefit.title} — ${benefit.partner}`}
        description={benefit.description}
        image={benefit.image_cover}
        url={window.location.href}
      />

      <Link to="/" className="benefit-detail__back-link">
        ← Volver a Beneficios
      </Link>

      <div className="benefit-detail__hero">
        <div className="benefit-detail__hero-content">
          <p className="benefit-detail__eyebrow">Beneficio exclusivo para la comunidad MonkeyFit</p>

          <div className="benefit-detail__identity">
            <div className="benefit-detail__logo-chip">
              {benefit.logo ? (
                <LazyImage
                  src={benefit.logo}
                  alt={`Logo de ${benefit.partner}`}
                  className="benefit-detail__logo-img"
                />
              ) : (
                <div className="benefit-detail__logo-initials">{getInitials(benefit.partner)}</div>
              )}
            </div>
            <div>
              {benefit.category && (
                <p className="benefit-detail__category-chip">{benefit.category.name}</p>
              )}
              <h1 className="benefit-detail__brand">{benefit.partner}</h1>
            </div>
          </div>

          {discountHeadline && (
            <p className="benefit-detail__discount-headline">{discountHeadline}</p>
          )}
          {validity && <p className="benefit-detail__validity">{validity}</p>}

          {benefit.promo_code && (
            <div className="benefit-detail__promo-code">
              <div>
                <span className="benefit-detail__promo-code-label">Código</span>
                <span className="benefit-detail__promo-code-value">{benefit.promo_code}</span>
              </div>
              <button
                type="button"
                className="benefit-detail__copy-btn"
                onClick={handleCopyCode}
                aria-live="polite"
              >
                {copied ? '¡Copiado!' : 'Copiar código'}
              </button>
            </div>
          )}

          {benefit.web && isValidUrl(benefit.web) && (
            <a
              href={benefit.web}
              target="_blank"
              rel="noopener noreferrer"
              className="benefit-detail__web-link"
            >
              Visitar sitio web ↗
            </a>
          )}

          <SocialIcons benefit={benefit} />
        </div>

        <LazyImage
          src={benefit.image_cover}
          alt={benefit.title}
          className="benefit-detail__hero-image"
        />
      </div>

      {benefit.conditions && (
        <section className="benefit-detail__section">
          <h2>Condiciones del beneficio</h2>
          <p>{benefit.conditions}</p>
          {benefit.legal_terms && (
            <p className="benefit-detail__legal-terms">{benefit.legal_terms}</p>
          )}
        </section>
      )}

      {infoLines.length > 0 && (
        <section className="benefit-detail__section">
          <h2>Información del beneficio</h2>
          {infoLines.map((line) => (
            <p key={line.label}>
              <strong>{line.label}:</strong>{' '}
              {line.label === 'Web' && isValidUrl(line.value) ? (
                <a
                  href={line.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="benefit-detail__info-link"
                >
                  {line.value}
                </a>
              ) : (
                line.value
              )}
            </p>
          ))}
        </section>
      )}

      {benefit.description && (
        <section className="benefit-detail__section">
          <h2>Conoce {benefit.partner}</h2>
          <p>{benefit.description}</p>
        </section>
      )}

      {benefit.web && isValidUrl(benefit.web) && (
        <section className="benefit-detail__cta-band">
          <h2>¿Listo para aprovechar tu beneficio?</h2>
          <a
            href={benefit.web}
            target="_blank"
            rel="noopener noreferrer"
            className="benefit-detail__cta-button"
          >
            Visitar {benefit.partner} ↗
          </a>
          <SocialIcons benefit={benefit} />
        </section>
      )}
    </div>
  );
};

export default BenefitDetail;
